import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import PostCard from '../components/PostCard';
import { PostListSkeleton } from '../components/Skeletons';
import PullToRefresh from '../components/PullToRefresh';
import { triggerHaptic } from '../utils/nativeBridge';
import { readCache, writeCache } from '../utils/localCache';

const Explore = () => {
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState('trending'); // 'trending' | 'radar' | 'forYou'
  const [timeframe, setTimeframe] = useState('24h'); // '24h' | '7d' | 'all'
  const [trendingTags, setTrendingTags] = useState([]);
  const [trendingLoading, setTrendingLoading] = useState(false);
  const [trendingExpanded, setTrendingExpanded] = useState(false);

  // Local Radar State
  const [radarCoords, setRadarCoords] = useState(null);
  const [radarLoading, setRadarLoading] = useState(false);
  const [radarError, setRadarError] = useState(null);
  const [radarPosts, setRadarPosts] = useState([]);
  const [radarRadius, setRadarRadius] = useState(50); // 50km

  // Posts Feed State
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const loadMoreRef = useRef(null);
  // True while showing cached posts that the in-flight fetch will replace
  const showingCachedRef = useRef(false);

  // Fetch Trending Hashtags
  const fetchTrendingTags = useCallback(async () => {
    const cachedTags = readCache(`trending:${timeframe}`);
    if (cachedTags?.length) setTrendingTags(cachedTags);
    else setTrendingLoading(true);
    try {
      const res = await axios.get(`/api/hashtags/trending?timeframe=${timeframe}&limit=10`);
      setTrendingTags(res.data?.hashtags || []);
      writeCache(`trending:${timeframe}`, res.data?.hashtags || []);
    } catch (err) {
      console.error('Fetch trending tags error:', err);
    } finally {
      setTrendingLoading(false);
    }
  }, [timeframe]);

  useEffect(() => {
    fetchTrendingTags();
  }, [fetchTrendingTags]);

  // Request Geolocation for Local Radar
  const requestLocation = () => {
    if (!navigator.geolocation) {
      setRadarError(t('explore.geoNotSupported', 'Geolocation is not supported by your browser.'));
      return;
    }
    setRadarLoading(true);
    setRadarError(null);

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setRadarCoords({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        });
        setRadarLoading(false);
      },
      (err) => {
        console.warn('Geolocation error:', err);
        setRadarError(t('explore.locationDenied', 'Please allow location access to see buzzes near you.'));
        setRadarLoading(false);
      },
      { timeout: 10000, maximumAge: 300000 }
    );
  };

  // Fetch Local Radar Posts
  const fetchRadarPosts = useCallback(async () => {
    if (!radarCoords) return;
    setRadarLoading(true);
    try {
      const res = await axios.get(
        `/api/hashtags/radar?lat=${radarCoords.lat}&lng=${radarCoords.lng}&radius_km=${radarRadius}`
      );
      setRadarPosts(res.data?.posts || []);
    } catch (err) {
      console.error('Fetch radar error:', err);
    } finally {
      setRadarLoading(false);
    }
  }, [radarCoords, radarRadius]);

  useEffect(() => {
    if (activeTab === 'radar' && radarCoords) {
      fetchRadarPosts();
    }
  }, [activeTab, radarCoords, fetchRadarPosts]);

  // Fetch Public Posts Feed for "For You" & "Trending"
  const fetchPosts = async (cursor = null, append = false) => {
    try {
      if (append) setLoadingMore(true);
      else if (!showingCachedRef.current) setLoading(true);

      const url = cursor
        ? `/api/posts/public?cursor=${cursor}&limit=20`
        : `/api/posts/public?limit=20`;
      const { data } = await axios.get(url);

      if (append) {
        setPosts((prev) => [...prev, ...data.posts]);
      } else {
        setPosts(data.posts || []);
        showingCachedRef.current = false;
        writeCache('explore:public', (data.posts || []).slice(0, 20));
      }
      setHasMore(data.hasMore === true);
      setNextCursor(data.nextCursor ?? null);
    } catch (err) {
      console.error('Explore fetch error:', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (activeTab !== 'radar') {
      if (posts.length === 0) {
        const cachedPosts = readCache('explore:public');
        if (cachedPosts?.length) {
          setPosts(cachedPosts);
          setHasMore(false); // no valid cursor until fresh data arrives
          setLoading(false);
          showingCachedRef.current = true;
        }
      }
      fetchPosts();
    }
  }, [activeTab]);

  const loadMore = () => {
    if (nextCursor && !loadingMore) fetchPosts(nextCursor, true);
  };

  useEffect(() => {
    if (!hasMore || loadingMore || loading) return;
    const el = loadMoreRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && nextCursor && !loadingMore) loadMore();
      },
      { rootMargin: '200px', threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, loadingMore, loading, nextCursor]);

  return (
    <div className="min-h-screen bg-white dark:bg-black">
      {/* Sticky Header with Navigation Tabs */}
      <div className="sticky top-[calc(3rem+env(safe-area-inset-top))] md:top-0 z-20 bg-white/90 dark:bg-black/90 backdrop-blur-md border-b border-zinc-100 dark:border-zinc-800">
        {/* Explore Navigation Tabs with Constrained Active Underline */}
        <div className="flex">
          <button
            type="button"
            onClick={() => {
              triggerHaptic('selection');
              setActiveTab('trending');
            }}
            className={`flex-1 py-3 text-center text-xs sm:text-sm font-semibold transition-all relative flex flex-col items-center justify-center gap-1 active:scale-95 touch-press ${
              activeTab === 'trending'
                ? 'text-indigo-600 dark:text-indigo-400 font-bold'
                : 'text-zinc-500 hover:text-zinc-800 dark:text-zinc-400 dark:hover:text-zinc-200 font-medium'
            }`}
          >
            <div className="flex items-center gap-1.5">
              <i className="fas fa-fire text-amber-500 text-xs" />
              <span>{t('explore.trending', 'Trending')}</span>
            </div>
            {activeTab === 'trending' && (
              <span className="absolute bottom-0 w-14 h-[3px] rounded-full bg-indigo-600 dark:bg-indigo-500 transition-all" />
            )}
          </button>

          <button
            type="button"
            onClick={() => {
              triggerHaptic('selection');
              setActiveTab('radar');
              if (!radarCoords) requestLocation();
            }}
            className={`flex-1 py-3 text-center text-xs sm:text-sm font-semibold transition-all relative flex flex-col items-center justify-center gap-1 active:scale-95 touch-press ${
              activeTab === 'radar'
                ? 'text-indigo-600 dark:text-indigo-400 font-bold'
                : 'text-zinc-500 hover:text-zinc-800 dark:text-zinc-400 dark:hover:text-zinc-200 font-medium'
            }`}
          >
            <div className="flex items-center gap-1.5">
              <i className="fas fa-satellite-dish text-xs" />
              <span>{t('explore.localRadar', 'Local Radar')}</span>
            </div>
            {activeTab === 'radar' && (
              <span className="absolute bottom-0 w-14 h-[3px] rounded-full bg-indigo-600 dark:bg-indigo-500 transition-all" />
            )}
          </button>

          <button
            type="button"
            onClick={() => {
              triggerHaptic('selection');
              setActiveTab('forYou');
            }}
            className={`flex-1 py-3 text-center text-xs sm:text-sm font-semibold transition-all relative flex flex-col items-center justify-center gap-1 active:scale-95 touch-press ${
              activeTab === 'forYou'
                ? 'text-indigo-600 dark:text-indigo-400 font-bold'
                : 'text-zinc-500 hover:text-zinc-800 dark:text-zinc-400 dark:hover:text-zinc-200 font-medium'
            }`}
          >
            <div className="flex items-center gap-1.5">
              <i className="far fa-compass text-xs" />
              <span>{t('explore.forYou', 'For You')}</span>
            </div>
            {activeTab === 'forYou' && (
              <span className="absolute bottom-0 w-14 h-[3px] rounded-full bg-indigo-600 dark:bg-indigo-500 transition-all" />
            )}
          </button>
        </div>
      </div>

      {/* Tab 1: Trending Hub */}
      {activeTab === 'trending' && (
        <div>
          {/* Trending Topics: Compact Space-Saving Bar */}
          <div className="py-2 px-3 sm:px-4 bg-zinc-50/70 dark:bg-zinc-950/70 border-b border-zinc-100 dark:border-zinc-800/80">
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <div className="flex items-center gap-1.5">
                <span className="font-bold text-[11px] uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
                  {t('explore.topTags', 'Top Tags')}
                </span>
              </div>

              <div className="flex items-center gap-1.5">
                {/* Timeframe Pill Selector */}
                <div className="flex items-center gap-0.5 bg-zinc-200/60 dark:bg-zinc-800/80 p-0.5 rounded-full text-[10px] font-semibold">
                  <button
                    type="button"
                    onClick={() => {
                      triggerHaptic('selection');
                      setTimeframe('24h');
                    }}
                    className={`px-2.5 py-0.5 rounded-full transition-all ${
                      timeframe === '24h'
                        ? 'bg-white dark:bg-zinc-700 text-zinc-900 dark:text-zinc-100 shadow-xs font-bold'
                        : 'text-zinc-500 dark:text-zinc-400 hover:text-zinc-800'
                    }`}
                  >
                    24h
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      triggerHaptic('selection');
                      setTimeframe('7d');
                    }}
                    className={`px-2.5 py-0.5 rounded-full transition-all ${
                      timeframe === '7d'
                        ? 'bg-white dark:bg-zinc-700 text-zinc-900 dark:text-zinc-100 shadow-xs font-bold'
                        : 'text-zinc-500 dark:text-zinc-400 hover:text-zinc-800'
                    }`}
                  >
                    7d
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      triggerHaptic('selection');
                      setTimeframe('all');
                    }}
                    className={`px-2.5 py-0.5 rounded-full transition-all ${
                      timeframe === 'all'
                        ? 'bg-white dark:bg-zinc-700 text-zinc-900 dark:text-zinc-100 shadow-xs font-bold'
                        : 'text-zinc-500 dark:text-zinc-400 hover:text-zinc-800'
                    }`}
                  >
                    All
                  </button>
                </div>

                {/* Grid Expand Toggle with proper 32px (w-8 h-8) tap target */}
                {trendingTags.length > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      triggerHaptic('light');
                      setTrendingExpanded((prev) => !prev);
                    }}
                    className="w-8 h-8 rounded-full flex items-center justify-center text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-200 hover:bg-zinc-200/60 dark:hover:bg-zinc-800/60 active:scale-90 transition-all touch-press"
                    title={trendingExpanded ? t('explore.compactView', 'Compact view') : t('explore.showAll', 'Show all')}
                    aria-label={trendingExpanded ? t('explore.compactView', 'Compact view') : t('explore.showAll', 'Show all')}
                  >
                    <i className={`fas ${trendingExpanded ? 'fa-compress-alt' : 'fa-th-large'} text-xs`} />
                  </button>
                )}
              </div>
            </div>

            {/* Trending Tags Display */}
            {trendingLoading ? (
              <div className="flex gap-2 overflow-hidden py-1">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="skeleton h-7 w-24 rounded-full flex-shrink-0" />
                ))}
              </div>
            ) : trendingTags.length === 0 ? (
              <p className="text-xs text-zinc-400 py-1">
                {t('explore.noTrendingTags', 'No trending tags found for this timeframe.')}
              </p>
            ) : trendingExpanded ? (
              /* Expanded Full Grid Mode */
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-1 pb-1">
                {trendingTags.map((tag) => (
                  <Link
                    key={tag.id}
                    to={`/hashtag/${encodeURIComponent(tag.name)}`}
                    onClick={() => triggerHaptic('light')}
                    className="p-2.5 rounded-xl bg-white dark:bg-zinc-900 border border-zinc-200/80 dark:border-zinc-800 hover:border-indigo-500/50 hover:shadow-xs transition-all flex flex-col justify-between group"
                  >
                    <div className="flex items-center justify-between gap-1 mb-1">
                      <span className="text-[10px] font-extrabold px-1.5 py-0.5 rounded-md bg-amber-50 dark:bg-amber-950/60 text-amber-600 dark:text-amber-400">
                        {tag.rank}
                      </span>
                      <span className="text-[10px] text-zinc-400 font-medium">
                        {tag.postCount} {t('explore.bazzes', 'bazzes')}
                      </span>
                    </div>
                    <span className="font-bold text-xs text-zinc-900 dark:text-zinc-100 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors truncate">
                      #{tag.name}
                    </span>
                  </Link>
                ))}
              </div>
            ) : (
              /* Sleek Compact Horizontal Reel (Default) */
              <div className="flex items-center gap-2 overflow-x-auto no-scrollbar py-1">
                {trendingTags.map((tag) => (
                  <Link
                    key={tag.id}
                    to={`/hashtag/${encodeURIComponent(tag.name)}`}
                    onClick={() => triggerHaptic('light')}
                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white dark:bg-zinc-900 border border-zinc-200/90 dark:border-zinc-800 hover:border-indigo-500/60 hover:bg-indigo-50/20 dark:hover:bg-indigo-950/20 active:bg-zinc-100 dark:active:bg-zinc-800 active:scale-95 touch-press transition-all flex-shrink-0 group shadow-2xs"
                  >
                    <span className="text-[11px] font-bold text-amber-500 dark:text-amber-400">{tag.rank} ·</span>
                    <span className="font-semibold text-xs text-zinc-800 dark:text-zinc-200 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                      #{tag.name}
                    </span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400 font-medium">
                      {tag.postCount}
                    </span>
                  </Link>
                ))}
                {trendingTags.length > 4 && (
                  <button
                    type="button"
                    onClick={() => {
                      triggerHaptic('light');
                      setTrendingExpanded(true);
                    }}
                    className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-zinc-100 dark:bg-zinc-800/80 hover:bg-zinc-200 dark:hover:bg-zinc-700 text-xs font-semibold text-zinc-600 dark:text-zinc-300 hover:text-indigo-600 dark:hover:text-indigo-400 flex-shrink-0 transition-colors"
                  >
                    <span>{t('common.seeAll', 'See all')}</span>
                    <i className="fas fa-arrow-right text-[10px]" />
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Trending Posts Feed */}
          <PullToRefresh onRefresh={fetchPosts}>
            <div className="divide-y divide-zinc-100 dark:divide-zinc-800">
              {loading ? (
                <PostListSkeleton />
              ) : posts.length === 0 ? (
                <div className="text-center py-12 text-zinc-400 text-sm">
                  {t('home.noPosts', 'No posts available')}
                </div>
              ) : (
                posts.map((post) => (
                  <div key={post.id} className="hover:bg-zinc-50/50 dark:hover:bg-zinc-950/50 transition-colors">
                    <PostCard post={post} onUpdate={fetchPosts} />
                  </div>
                ))
              )}

              {hasMore && (
                <div ref={loadMoreRef} className="py-6 flex justify-center">
                  {loadingMore && (
                    <div className="w-5 h-5 border-2 border-indigo-600 dark:border-indigo-400 border-t-transparent rounded-full animate-spin" />
                  )}
                </div>
              )}
            </div>
          </PullToRefresh>
        </div>
      )}

      {/* Tab 2: Local Radar */}
      {activeTab === 'radar' && (
        <div className="p-4 space-y-4">
          {/* Radar Header & Controls */}
          <div className="p-4 rounded-2xl bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-indigo-500/5 border border-indigo-500/20 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-indigo-500" />
                </span>
                <h3 className="font-bold text-sm text-zinc-900 dark:text-zinc-100">
                  {t('explore.radarActive', 'Local Radar active')}
                </h3>
              </div>
              <p className="text-xs text-zinc-500 dark:text-zinc-400">
                {t('explore.radarSubtitle', 'Discovering posts and trending topics around your location')}
              </p>
            </div>

            {/* Radius Selector */}
            {radarCoords && (
              <div className="flex items-center gap-1.5 self-start sm:self-auto">
                <span className="text-xs text-zinc-500 font-medium">Radius:</span>
                {[15, 30, 50, 100].map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setRadarRadius(r)}
                    className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-all ${
                      radarRadius === r
                        ? 'bg-indigo-600 text-white shadow-xs'
                        : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-200'
                    }`}
                  >
                    {r}km
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Location Request / Error Banner */}
          {!radarCoords && (
            <div className="text-center py-12 px-4 rounded-2xl border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900">
              <div className="w-12 h-12 rounded-full bg-indigo-500/10 text-indigo-600 flex items-center justify-center mx-auto mb-3">
                <i className="fas fa-location-arrow text-xl" />
              </div>
              <p className="font-semibold text-sm text-zinc-900 dark:text-zinc-100 mb-1">
                {t('explore.enableLocationTitle', 'Enable Location for Local Radar')}
              </p>
              <p className="text-xs text-zinc-500 max-w-sm mx-auto mb-4">
                {radarError || t('explore.enableLocationDesc', 'Connect with nearby creators and see what is buzzing right in your neighborhood.')}
              </p>
              <button
                type="button"
                onClick={requestLocation}
                disabled={radarLoading}
                className="px-5 py-2.5 rounded-full text-xs font-semibold bg-indigo-600 hover:bg-indigo-700 text-white transition-all shadow-xs disabled:opacity-50"
              >
                {radarLoading ? t('common.loading', 'Locating...') : t('explore.enableLocation', 'Turn On Local Radar')}
              </button>
            </div>
          )}

          {/* Radar Posts List */}
          {radarCoords && (
            <div className="divide-y divide-zinc-100 dark:divide-zinc-800">
              {radarLoading ? (
                <div>
                  <p className="text-xs text-zinc-400 text-center pt-4">{t('explore.scanningRadar', 'Scanning nearby buzzes...')}</p>
                  <PostListSkeleton count={3} />
                </div>
              ) : radarPosts.length === 0 ? (
                <div className="text-center py-12 text-zinc-400 text-xs">
                  {t('explore.noRadarPosts', 'No posts found within this radius yet. Try increasing the radius!')}
                </div>
              ) : (
                radarPosts.map((post) => (
                  <div key={post.id} className="relative">
                    {post.distanceKm && (
                      <span className="absolute top-3 right-4 z-10 px-2 py-0.5 rounded-full bg-indigo-50 dark:bg-indigo-950/70 border border-indigo-200 dark:border-indigo-800 text-[10px] font-bold text-indigo-600 dark:text-indigo-400">
                        📍 {post.distanceKm} km away
                      </span>
                    )}
                    <PostCard post={post} onUpdate={fetchRadarPosts} />
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {/* Tab 3: For You Feed */}
      {activeTab === 'forYou' && (
        <div className="divide-y divide-zinc-100 dark:divide-zinc-800">
          {loading ? (
            <PostListSkeleton />
          ) : posts.length === 0 ? (
            <div className="text-center py-12 text-zinc-400 text-sm">
              {t('home.noPosts', 'No posts available')}
            </div>
          ) : (
            posts.map((post) => (
              <div key={post.id} className="hover:bg-zinc-50/50 dark:hover:bg-zinc-950/50 transition-colors">
                <PostCard post={post} onUpdate={fetchPosts} />
              </div>
            ))
          )}

          {hasMore && (
            <div ref={loadMoreRef} className="py-6 flex justify-center">
              {loadingMore && (
                <div className="w-5 h-5 border-2 border-indigo-600 dark:border-indigo-400 border-t-transparent rounded-full animate-spin" />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Explore;
